import { useState, useEffect } from 'react';
import { Save, Loader2, CheckCircle2, X, Zap, Power } from 'lucide-react';
import { appApi, adNetworkApi } from '@/api/client';
import type { App, AdNetworkConfig } from '@/types';

// ============ 平台配置（简化版：只保留名称、标签、ID字段）============
interface SlotField { key: string; label: string; placeholder: string }
interface PlatformDef {
  label: string;
  tag: string;           // '个人可接入' | '需公司资质'
  tagOk: boolean;        // true=绿色(个人可接) false=橙色(需资质)
  tip: string;           // 一句话说明
  appIdKey: string;
  appIdLabel: string;
  appIdPlaceholder: string;
  slots: SlotField[];
}

const PLATFORMS: Record<string, PlatformDef> = {
  mango: {
    label: '芒果联盟', tag: '个人可接入', tagOk: true,
    tip: '聚合穿山甲/优量汇/快手等20+联盟，填充率99%，推荐',
    appIdKey: 'mango_app_id', appIdLabel: '芒果媒体ID', appIdPlaceholder: '如 100023',
    slots: [
      { key: 'mango_splash_id', label: '开屏广告位ID', placeholder: '应用启动全屏广告' },
      { key: 'mango_banner_id', label: '横幅广告位ID', placeholder: '页面横幅广告' },
      { key: 'mango_interstitial_id', label: '插屏广告位ID', placeholder: '弹窗插屏广告' },
      { key: 'mango_reward_video_id', label: '激励视频广告位ID', placeholder: '看完发奖视频' },
      { key: 'mango_short_video_id', label: '短视频广告位ID', placeholder: '短视频信息流广告' },
      { key: 'mango_fullscreen_video_id', label: '全屏视频广告位ID', placeholder: '全屏视频广告' },
      { key: 'mango_feed_id', label: '信息流广告位ID', placeholder: '内容列表中插入广告' },
    ],
  },
  baiyi: {
    label: '百益聚合', tag: '个人可接入', tagOk: true,
    tip: '无需软著、未上架也能接，月结',
    appIdKey: 'baiyi_app_id', appIdLabel: '百益媒体ID', appIdPlaceholder: '百益后台创建媒体后获取',
    slots: [
      { key: 'baiyi_splash_id', label: '开屏广告位ID', placeholder: '应用启动全屏广告' },
      { key: 'baiyi_banner_id', label: '横幅广告位ID', placeholder: '页面横幅广告' },
      { key: 'baiyi_interstitial_id', label: '插屏广告位ID', placeholder: '弹窗插屏广告' },
      { key: 'baiyi_reward_video_id', label: '激励视频广告位ID', placeholder: '看完发奖视频' },
      { key: 'baiyi_short_video_id', label: '短视频广告位ID', placeholder: '短视频信息流广告' },
      { key: 'baiyi_fullscreen_video_id', label: '全屏视频广告位ID', placeholder: '全屏视频广告' },
      { key: 'baiyi_feed_id', label: '信息流广告位ID', placeholder: '内容列表中插入广告' },
    ],
  },
  pocket: {
    label: '口袋工厂', tag: '个人可接入', tagOk: true,
    tip: '数据透明，聚合优量汇/穿山甲/快手，日结',
    appIdKey: 'pocket_app_id', appIdLabel: '口袋工厂媒体ID', appIdPlaceholder: '口袋工厂后台创建媒体后获取',
    slots: [
      { key: 'pocket_splash_id', label: '开屏广告位ID', placeholder: '应用启动全屏广告' },
      { key: 'pocket_banner_id', label: '横幅广告位ID', placeholder: '页面横幅广告' },
      { key: 'pocket_interstitial_id', label: '插屏广告位ID', placeholder: '弹窗插屏广告' },
      { key: 'pocket_reward_video_id', label: '激励视频广告位ID', placeholder: '看完发奖视频' },
      { key: 'pocket_short_video_id', label: '短视频广告位ID', placeholder: '短视频信息流广告' },
      { key: 'pocket_fullscreen_video_id', label: '全屏视频广告位ID', placeholder: '全屏视频广告' },
      { key: 'pocket_feed_id', label: '信息流广告位ID', placeholder: '内容列表中插入广告' },
    ],
  },
  adsense: {
    label: 'Google AdSense', tag: '个人可接入', tagOk: true,
    tip: '网站/H5专用，收益最高',
    appIdKey: 'adsense_client_id', appIdLabel: 'AdSense发布商ID', appIdPlaceholder: '如 ca-pub-1234567890123456',
    slots: [
      { key: 'adsense_preroll_slot', label: '视频广告单元ID', placeholder: '前贴片视频广告' },
      { key: 'adsense_splash_slot', label: '全屏广告单元ID', placeholder: '页面切换全屏广告' },
      { key: 'adsense_banner_slot', label: '展示广告单元ID', placeholder: '横幅/信息流广告' },
    ],
  },
  baidu: {
    label: '百度联盟', tag: '个人可接入', tagOk: true,
    tip: '网站/H5专用，满100元月结',
    appIdKey: 'baidu_union_id', appIdLabel: '百度联盟用户ID', appIdPlaceholder: '如 12345678',
    slots: [
      { key: 'baidu_preroll_slot', label: '贴片广告位ID', placeholder: '正片播放前视频广告' },
      { key: 'baidu_splash_slot', label: '浮窗广告位ID', placeholder: '页面浮窗广告' },
      { key: 'baidu_banner_slot', label: '横幅广告位ID', placeholder: '页面内嵌横幅' },
    ],
  },
  csj: {
    label: '穿山甲', tag: '需公司资质', tagOk: false,
    tip: '字节跳动，需公司营业执照',
    appIdKey: 'csj_app_id', appIdLabel: '穿山甲应用ID', appIdPlaceholder: '如 5001121',
    slots: [
      { key: 'csj_preroll_slot', label: '前贴片广告位ID', placeholder: '正片播放前视频广告' },
      { key: 'csj_splash_slot', label: '开屏广告位ID', placeholder: '应用启动全屏广告' },
      { key: 'csj_interstitial_slot', label: '插屏广告位ID', placeholder: '页面切换弹窗广告' },
    ],
  },
  ylh: {
    label: '优量汇', tag: '需公司资质', tagOk: false,
    tip: '腾讯广告，需公司营业执照',
    appIdKey: 'ylh_app_id', appIdLabel: '优量汇应用ID', appIdPlaceholder: '如 1100000000',
    slots: [
      { key: 'ylh_preroll_slot', label: '前贴片广告位ID', placeholder: '正片播放前视频广告' },
      { key: 'ylh_splash_slot', label: '开屏广告位ID', placeholder: '应用启动全屏广告' },
      { key: 'ylh_interstitial_slot', label: '插屏广告位ID', placeholder: '页面切换弹窗广告' },
    ],
  },
};

export default function AdNetwork() {
  const [apps, setApps] = useState<App[]>([]);
  const [selectedAppId, setSelectedAppId] = useState('');
  const [config, setConfig] = useState<AdNetworkConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<{ msg: string; ok: boolean } | null>(null);

  const showToast = (msg: string, ok = true) => {
    setToast({ msg, ok });
    setTimeout(() => setToast(null), 2500);
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      const res = await appApi.getAll();
      setApps(res.data || []);
      if (res.data && res.data.length > 0) setSelectedAppId(res.data[0].id);
      setLoading(false);
    })();
  }, []);

  useEffect(() => {
    if (!selectedAppId) return;
    (async () => {
      const res = await adNetworkApi.get(selectedAppId);
      setConfig(res.data);
    })();
  }, [selectedAppId]);

  const update = (patch: Partial<AdNetworkConfig>) => {
    setConfig((prev) => ({ ...(prev || {} as AdNetworkConfig), ...patch }) as AdNetworkConfig);
  };

  const handleSave = async () => {
    if (!config || !selectedAppId) return;
    setSaving(true);
    try {
      await adNetworkApi.update({ ...config, app_id: selectedAppId });
      showToast('✓ 已保存，广告配置立即生效');
    } catch {
      showToast('保存失败，请重试', false);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  if (apps.length === 0) {
    return (
      <div className="bg-slate-800 rounded-xl p-8 text-center text-slate-400">
        <p>请先在「应用管理」中创建应用</p>
      </div>
    );
  }

  const platform = config?.platform || 'none';
  const activePlatform = PLATFORMS[platform];

  return (
    <div className="space-y-5 max-w-4xl">
      {/* 标题 + 应用选择 */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h2 className="text-2xl font-bold text-white">广告联盟</h2>
          <p className="text-slate-400 text-sm mt-1">选平台 → 填ID → 开开关 → 保存，广告自动加载</p>
        </div>
        <select
          value={selectedAppId}
          onChange={(e) => setSelectedAppId(e.target.value)}
          className="bg-slate-700 text-white border border-slate-600 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-[200px]"
        >
          {apps.map((a) => (
            <option key={a.id} value={a.id}>{a.name}</option>
          ))}
        </select>
      </div>

      {/* 简单3步指引 */}
      <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4 flex items-center gap-4">
        <div className="flex items-center gap-2 text-sm">
          <span className="w-6 h-6 rounded-full bg-blue-500 text-white text-xs flex items-center justify-center font-bold">1</span>
          <span className="text-slate-300">选平台</span>
        </div>
        <span className="text-slate-600">→</span>
        <div className="flex items-center gap-2 text-sm">
          <span className="w-6 h-6 rounded-full bg-blue-500 text-white text-xs flex items-center justify-center font-bold">2</span>
          <span className="text-slate-300">填广告位ID</span>
        </div>
        <span className="text-slate-600">→</span>
        <div className="flex items-center gap-2 text-sm">
          <span className="w-6 h-6 rounded-full bg-blue-500 text-white text-xs flex items-center justify-center font-bold">3</span>
          <span className="text-slate-300">开开关 + 保存</span>
        </div>
      </div>

      {config && (
        <>
          {/* 第1步：选择平台 */}
          <div className="bg-slate-800 rounded-2xl border border-slate-700 p-5">
            <h3 className="text-white font-semibold mb-1">第 1 步：选择广告平台</h3>
            <p className="text-xs text-slate-500 mb-4">点击你要用的平台即可（个人推荐芒果联盟）</p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <button
                onClick={() => update({ platform: 'none' })}
                className={`text-left p-4 rounded-xl border-2 transition-all ${platform === 'none' ? 'border-slate-500 bg-slate-700/50' : 'border-slate-700 bg-slate-700/20 hover:border-slate-600'}`}
              >
                <div className="font-semibold text-sm text-slate-300">不接入</div>
                <div className="text-xs text-slate-500 mt-1">只用自托管广告</div>
              </button>
              {Object.entries(PLATFORMS).map(([key, info]) => {
                const active = platform === key;
                return (
                  <button
                    key={key}
                    onClick={() => update({ platform: key })}
                    className={`text-left p-4 rounded-xl border-2 transition-all ${active ? 'border-blue-500 bg-blue-600/15' : 'border-slate-700 bg-slate-700/20 hover:border-slate-600'}`}
                  >
                    <div className="flex items-center justify-between gap-1 mb-1">
                      <span className={`font-semibold text-sm ${active ? 'text-blue-400' : 'text-white'}`}>{info.label}</span>
                      <span className={`text-[9px] px-1.5 py-0.5 rounded ${info.tagOk ? 'bg-green-500/20 text-green-400' : 'bg-amber-500/20 text-amber-400'}`}>
                        {info.tag}
                      </span>
                    </div>
                    <div className="text-xs text-slate-500">{info.tip}</div>
                    {active && (
                      <div className="text-[10px] text-blue-400 mt-1 flex items-center gap-1">
                        <CheckCircle2 className="w-3 h-3" /> 已选择
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* 第2步：填入广告位ID */}
          {activePlatform && (
            <div className="bg-slate-800 rounded-2xl border border-slate-700 p-5">
              <h3 className="text-white font-semibold mb-1">第 2 步：填入 {activePlatform.label} 的广告位 ID</h3>
              <p className="text-xs text-slate-500 mb-4">在 {activePlatform.label} 后台创建媒体和广告位后，把 ID 复制到这里</p>

              {/* 媒体ID */}
              <div className="mb-4">
                <label className="block text-sm text-slate-300 mb-1.5">{activePlatform.appIdLabel}</label>
                <input
                  type="text"
                  value={(config as any)[activePlatform.appIdKey] || ''}
                  onChange={(e) => update({ [activePlatform.appIdKey]: e.target.value } as any)}
                  placeholder={activePlatform.appIdPlaceholder}
                  className="w-full bg-slate-900 text-white text-sm border border-slate-600 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                />
              </div>

              {/* 广告位ID */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {activePlatform.slots.map((slot) => (
                  <div key={slot.key}>
                    <label className="block text-sm text-slate-300 mb-1.5">{slot.label}</label>
                    <input
                      type="text"
                      value={(config as any)[slot.key] || ''}
                      onChange={(e) => update({ [slot.key]: e.target.value } as any)}
                      placeholder={slot.placeholder}
                      className="w-full bg-slate-900 text-white text-sm border border-slate-600 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* 第3步：开关 + 保存 */}
          <div className="bg-slate-800 rounded-2xl border border-slate-700 p-5 space-y-4">
            <h3 className="text-white font-semibold">第 3 步：开启开关并保存</h3>

            {/* 总开关 */}
            <div className="flex items-center justify-between bg-slate-900/50 rounded-xl p-4">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${config.enabled ? 'bg-green-600/20' : 'bg-slate-600/20'}`}>
                  <Power className={`w-5 h-5 ${config.enabled ? 'text-green-400' : 'text-slate-500'}`} />
                </div>
                <div>
                  <div className="text-white font-semibold">广告联盟开关</div>
                  <div className="text-xs text-slate-500">{config.enabled ? '已开启 — 广告会自动加载' : '关闭中 — 开启后广告才会显示'}</div>
                </div>
              </div>
              <button
                onClick={() => update({ enabled: !config.enabled })}
                className={`relative w-14 h-7 rounded-full transition-colors flex-shrink-0 ${config.enabled ? 'bg-green-600' : 'bg-slate-600'}`}
              >
                <span className={`absolute top-0.5 left-0.5 w-6 h-6 bg-white rounded-full transition-transform ${config.enabled ? 'translate-x-7' : ''}`} />
              </button>
            </div>

            {/* 回退开关 */}
            <div className="flex items-center justify-between bg-slate-900/50 rounded-xl p-4">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${config.fallback_to_self ? 'bg-blue-600/20' : 'bg-slate-600/20'}`}>
                  <Zap className={`w-5 h-5 ${config.fallback_to_self ? 'text-blue-400' : 'text-slate-500'}`} />
                </div>
                <div>
                  <div className="text-white font-semibold">广告兜底开关</div>
                  <div className="text-xs text-slate-500">联盟广告没填充时，自动显示你自己的广告（建议开启）</div>
                </div>
              </div>
              <button
                onClick={() => update({ fallback_to_self: !config.fallback_to_self })}
                className={`relative w-14 h-7 rounded-full transition-colors flex-shrink-0 ${config.fallback_to_self ? 'bg-blue-600' : 'bg-slate-600'}`}
              >
                <span className={`absolute top-0.5 left-0.5 w-6 h-6 bg-white rounded-full transition-transform ${config.fallback_to_self ? 'translate-x-7' : ''}`} />
              </button>
            </div>

            {/* 保存按钮 */}
            <button
              onClick={handleSave}
              disabled={saving}
              className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white font-medium rounded-xl transition-colors text-lg"
            >
              {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
              保存配置
            </button>
          </div>

          {/* 程序接入提示（折叠式，不显眼） */}
          <details className="bg-slate-800/50 rounded-xl border border-slate-700/50 p-4">
            <summary className="text-sm text-slate-400 cursor-pointer hover:text-slate-300">
              程序怎么接入广告？（点开看）
            </summary>
            <div className="mt-3 space-y-3 text-sm">
              <div className="bg-slate-900 rounded-lg p-3">
                <p className="text-slate-400 text-xs mb-2">网站 / H5 程序：在网页加一行代码</p>
                <pre className="text-green-400 text-xs overflow-x-auto"><code>{`<script src="https://unified-auth-admin.pages.dev/sdk/ad-widget.js" data-app-id="${selectedAppId}" defer></script>`}</code></pre>
              </div>
              <div className="bg-slate-900 rounded-lg p-3">
                <p className="text-slate-400 text-xs mb-2">Android APP：用 android-sdk 模块，Application 里加一行</p>
                <pre className="text-green-400 text-xs overflow-x-auto"><code>{`AdNetworkManager.init(this, "${selectedAppId}", "https://unified-auth-admin.pages.dev");`}</code></pre>
                <p className="text-slate-500 text-xs mt-1">详细接入见项目 <code className="text-yellow-300">android-sdk/README.md</code></p>
              </div>
              <p className="text-slate-500 text-xs">程序启动后会自动读取上面的配置，自动加载对应平台的广告，无需手动处理。</p>
            </div>
          </details>
        </>
      )}

      {toast && (
        <div className={`fixed bottom-6 right-6 flex items-center gap-2 px-5 py-3 rounded-lg shadow-lg ${toast.ok ? 'bg-emerald-600' : 'bg-red-600'} text-white text-sm`}>
          {toast.ok ? <CheckCircle2 className="w-5 h-5" /> : <X className="w-5 h-5" />}
          {toast.msg}
        </div>
      )}
    </div>
  );
}
